/**
 * Navigation Settings Slice for appStore
 *
 * Single responsibility: Manage navigation settings state and actions
 * Integrates with Zustand store (same pattern as appStore)
 * Handles loading, saving, resetting navigation configuration
 */

import { create } from 'zustand'
import { NavigationSettings, DEFAULT_NAVIGATION_SETTINGS, SETTINGS_CONSTRAINTS } from '../types/NavigationSettings'

/**
 * Navigation Settings Store Interface
 */
export interface NavigationSettingsStore {
  // State
  settings: NavigationSettings
  isLoading: boolean
  error: string | null

  // Actions
  setSettings: (settings: Partial<NavigationSettings>) => void
  resetSettings: () => void
  loadSettings: () => Promise<void>
  saveSettings: () => Promise<void>
  updateSingleSetting: <K extends keyof NavigationSettings>(
    key: K,
    value: NavigationSettings[K]
  ) => void
}

/**
 * LocalStorage key for persisting settings
 */
const STORAGE_KEY = 'dukkan_navigation_settings'

/**
 * Validate settings against constraints
 * Returns corrected settings if any values are invalid
 */
const validateSettings = (settings: Partial<NavigationSettings>): Partial<NavigationSettings> => {
  const validated = { ...settings }

  // Validate distanceThresholdPercent
  if (typeof validated.distanceThresholdPercent === 'number') {
    const { min, max } = SETTINGS_CONSTRAINTS.distanceThresholdPercent
    if (validated.distanceThresholdPercent < min || validated.distanceThresholdPercent > max) {
      console.warn(
        `⚠️ distanceThresholdPercent ${validated.distanceThresholdPercent} out of range [${min}, ${max}], clamping`
      )
      validated.distanceThresholdPercent = Math.max(min, Math.min(max, validated.distanceThresholdPercent))
    }
  }

  // Validate max3DDistance
  if (typeof validated.max3DDistance === 'number') {
    const { min, max } = SETTINGS_CONSTRAINTS.max3DDistance
    if (validated.max3DDistance < min || validated.max3DDistance > max) {
      console.warn(
        `⚠️ max3DDistance ${validated.max3DDistance} out of range [${min}, ${max}], clamping`
      )
      validated.max3DDistance = Math.max(min, Math.min(max, validated.max3DDistance))
    }
  }

  // Validate maxDeltaAngle
  if (typeof validated.maxDeltaAngle === 'number') {
    const { min, max } = SETTINGS_CONSTRAINTS.maxDeltaAngle
    if (validated.maxDeltaAngle < min || validated.maxDeltaAngle > max) {
      console.warn(
        `⚠️ maxDeltaAngle ${validated.maxDeltaAngle} out of range [${min}, ${max}], clamping`
      )
      validated.maxDeltaAngle = Math.max(min, Math.min(max, validated.maxDeltaAngle))
    }
  }

  // Validate maxCandidates (must be null or positive integer)
  if (validated.maxCandidates !== null && typeof validated.maxCandidates === 'number') {
    const { min } = SETTINGS_CONSTRAINTS.maxCandidates
    if (validated.maxCandidates < min) {
      console.warn(`⚠️ maxCandidates ${validated.maxCandidates} less than minimum ${min}, setting to null`)
      validated.maxCandidates = null
    }
  }

  // Validate sequentialWindow
  if (typeof validated.sequentialWindow === 'number') {
    const { min, max } = SETTINGS_CONSTRAINTS.sequentialWindow
    if (validated.sequentialWindow < min || validated.sequentialWindow > max) {
      console.warn(
        `⚠️ sequentialWindow ${validated.sequentialWindow} out of range [${min}, ${max}], clamping`
      )
      validated.sequentialWindow = Math.max(min, Math.min(max, validated.sequentialWindow))
    }
  }

  // Validate nearWindow
  if (typeof validated.nearWindow === 'number') {
    const { min, max } = SETTINGS_CONSTRAINTS.nearWindow
    if (validated.nearWindow < min || validated.nearWindow > max) {
      console.warn(
        `⚠️ nearWindow ${validated.nearWindow} out of range [${min}, ${max}], clamping`
      )
      validated.nearWindow = Math.max(min, Math.min(max, validated.nearWindow))
    }
  }

  // Validate contextualWindow
  if (typeof validated.contextualWindow === 'number') {
    const { min, max } = SETTINGS_CONSTRAINTS.contextualWindow
    if (validated.contextualWindow < min || validated.contextualWindow > max) {
      console.warn(
        `⚠️ contextualWindow ${validated.contextualWindow} out of range [${min}, ${max}], clamping`
      )
      validated.contextualWindow = Math.max(min, Math.min(max, validated.contextualWindow))
    }
  }

  return validated
}

/**
 * Navigation Settings Store
 * Uses Zustand for state management
 * Persists to localStorage automatically
 */
export const useNavigationSettingsStore = create<NavigationSettingsStore>((set, get) => ({
  settings: DEFAULT_NAVIGATION_SETTINGS,
  isLoading: false,
  error: null,

  /**
   * Update settings (partial update, merges with existing)
   */
  setSettings: (newSettings: Partial<NavigationSettings>) => {
    const validated = validateSettings(newSettings)
    return set((state) => ({
      settings: { ...state.settings, ...validated },
      error: null,
    }))
  },

  /**
   * Reset to default settings
   */
  resetSettings: () =>
    set({
      settings: { ...DEFAULT_NAVIGATION_SETTINGS },
      error: null,
    }),

  /**
   * Update a single setting by key
   */
  updateSingleSetting: <K extends keyof NavigationSettings>(
    key: K,
    value: NavigationSettings[K]
  ) => {
    const validated = validateSettings({ [key]: value })
    return set((state) => ({
      settings: { ...state.settings, ...validated },
      error: null,
    }))
  },

  /**
   * Load settings from localStorage
   * Called on app startup
   */
  loadSettings: async () => {
    set({ isLoading: true, error: null })
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<NavigationSettings>
        // Validate and merge with defaults to ensure all required fields exist
        const validated = validateSettings(parsed)
        const merged = { ...DEFAULT_NAVIGATION_SETTINGS, ...validated }
        set({
          settings: merged,
          isLoading: false,
          error: null,
        })
      } else {
        set({ isLoading: false, error: null })
      }
    } catch (err) {
      set({
        isLoading: false,
        error: `Failed to load settings: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    }
  },

  /**
   * Save current settings to localStorage
   * Called whenever settings change (or on demand)
   */
  saveSettings: async () => {
    set({ isLoading: true, error: null })
    try {
      const state = get()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.settings))
      set({
        isLoading: false,
        error: null,
      })
    } catch (err) {
      set({
        isLoading: false,
        error: `Failed to save settings: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    }
  },
}))

/**
 * Load settings on module initialization (auto-restore persisted settings)
 */
if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<NavigationSettings>
      const validated = validateSettings(parsed)
      const merged = { ...DEFAULT_NAVIGATION_SETTINGS, ...validated }
      useNavigationSettingsStore.setState({ settings: merged })
      console.log('✅ Navigation settings loaded from localStorage')
    }
  } catch (err) {
    console.error('⚠️ Failed to load navigation settings:', err)
  }
}
